import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, MapPin, Lock } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { CONTACT, IMAGES } from "@/lib/site";

const navItems = [
  { to: "/", label: "Home", end: true },
  { to: "/what-we-offer", label: "What We Offer" },
  { to: "/who-we-are", label: "Who We Are" },
  { to: "/free-quote", label: "Free Quote" },
];

const Logo = ({ dark = true }) => (
  <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
    <div className="rounded-xl overflow-hidden bg-black ring-1 ring-white/10 shadow-sm">
      <img src={IMAGES.logo} alt="Shine and Line Services logo" className="h-11 w-11 object-cover" />
    </div>
    <div className="leading-tight">
      <div className={`font-heading font-bold text-base ${dark ? "text-white" : "text-[#111827]"}`}>Shine &amp; Line</div>
      <div className={`text-[10px] uppercase tracking-widest ${dark ? "text-[#9CA3AF]" : "text-[#4B5563]"}`}>Services · Rangiora</div>
    </div>
  </Link>
);

export default function Layout() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  return (
    <div className="min-h-screen flex flex-col bg-[#F9F9FB] text-[#111827]">
      <header className="sticky top-0 z-50 bg-[#0A0A0B] border-b border-white/10" data-testid="site-header">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm transition-colors ${
                    isActive ? "text-white bg-white/10" : "text-[#9CA3AF] hover:text-white hover:bg-white/5"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/booking"
              data-testid="nav-book-now"
              className="ml-2 pill-btn inline-flex items-center gap-2 rounded-full bg-[#0EA5E9] hover:bg-[#38BDF8] text-white text-sm font-medium px-5 py-2.5"
            >
              Book Now
            </Link>
          </nav>
          <button
            onClick={() => setOpen((o) => !o)}
            className="md:hidden p-2 rounded-lg text-white"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-white/10 bg-[#0A0A0B]" data-testid="mobile-nav">
            <div className="px-6 py-4 flex flex-col gap-1">
              {navItems.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-xl text-sm ${isActive ? "text-white bg-white/10" : "text-[#D1D5DB] hover:bg-white/5"}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to="/booking"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-book-now"
                className="mt-2 pill-btn text-center rounded-full bg-[#0EA5E9] text-white text-sm font-medium px-5 py-3"
              >
                Book Now
              </Link>
            </div>
          </div>
        )}
      </header>

      <main key={loc.pathname} className="flex-1">
        <Outlet />
      </main>

      <footer className="mt-16 bg-[#0A0A0B] text-[#D1D5DB]" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12 grid gap-10 md:grid-cols-3">
          <div>
            <Logo />
            <p className="mt-4 text-sm text-[#9CA3AF] leading-relaxed max-w-xs">
              Pristine pressure washing and warm LED lighting for Rangiora homes. Locally owned and proudly Kiwi.
            </p>
          </div>
          <div>
            <div className="font-heading font-semibold text-sm uppercase tracking-widest text-white mb-3">Contact</div>
            <ul className="space-y-2 text-sm text-[#9CA3AF]">
              <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#0EA5E9]" /> <a className="hover:text-white" href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} data-testid="footer-phone">{CONTACT.phone}</a></li>
              <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#0EA5E9]" /> <a className="hover:text-white" href={`mailto:${CONTACT.email}`} data-testid="footer-email">{CONTACT.email}</a></li>
              <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#0EA5E9]" /> {CONTACT.location}</li>
            </ul>
          </div>
          <div>
            <div className="font-heading font-semibold text-sm uppercase tracking-widest text-white mb-3">Quick links</div>
            <ul className="space-y-2 text-sm text-[#9CA3AF]">
              {navItems.map((n) => (
                <li key={n.to}><Link to={n.to} className="hover:text-white">{n.label}</Link></li>
              ))}
              <li><Link to="/booking" className="hover:text-white">Book Now</Link></li>
              <li><Link to="/admin" className="hover:text-white inline-flex items-center gap-1.5" data-testid="footer-admin-link"><Lock className="w-3 h-3" /> Admin</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 py-5 text-center text-xs text-[#6B7280]">
          © {new Date().getFullYear()} Shine and Line Services. All rights reserved.
        </div>
      </footer>
      <Toaster position="top-center" richColors />
    </div>
  );
}
